import { useCallback, useMemo, useState } from 'react'

import { composeRecurrenceId } from '@/server-data'

import { Button, Stack } from '@mui/material'

import { RecurrencePicker } from '../RecurrencePicker'
import { useTexts } from './useTexts'

type Props = {
  disabled?: boolean
  onCancel: () => void
  onCreate: (r: uui.domain.rm.Recurrence) => void
  onOpenExisting: (r: uui.domain.rm.Recurrence) => void
  recurrenceMap:
    | uui.domain.helpers.RequireAtLeastOne<
        Record<uui.domain.rm.RecurrenceTypes, uui.domain.rm.Recurrence[]>
      >
    | undefined
}

export function RecurrenceSelector(props: Props) {
  const { recurrenceMap, onCreate, onOpenExisting, onCancel, disabled } = props
  const texts = useTexts()

  const [recurrence, setRecurrence] = useState<uui.domain.rm.Recurrence | undefined>(undefined)

  const selectingAlreadyExistent = useMemo<boolean>(() => {
    if (!recurrence || !recurrenceMap) return false
    return (
      recurrenceMap[recurrence.type]?.some(
        r =>
          composeRecurrenceId(r.type, r.value) ===
          composeRecurrenceId(recurrence.type, recurrence.value),
      ) || false
    )
  }, [recurrenceMap, recurrence])

  const handleOnConfirm = useCallback(() => {
    if (!recurrence) return
    selectingAlreadyExistent ? onOpenExisting(recurrence) : onCreate(recurrence)
  }, [onCreate, onOpenExisting, selectingAlreadyExistent, recurrence])

  return (
    <Stack
      data-testid="recurrence-selector-root"
      direction="column"
      width="100%"
      height="auto"
      flexShrink={0}
      paddingX={3}
      paddingY={3}
    >
      <RecurrencePicker value={recurrence} onChange={setRecurrence} recurrenceMap={recurrenceMap} />

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button variant="text" size="medium" color="primary" onClick={onCancel}>
          {texts.cancelTranslation}
        </Button>
        <Button
          size="medium"
          color="primary"
          variant="contained"
          onClick={handleOnConfirm}
          disabled={!recurrence || disabled}
        >
          {selectingAlreadyExistent
            ? texts.openExistingTranslation
            : texts.setRecurrenceTranslation}
        </Button>
      </Stack>
    </Stack>
  )
}
